import * as React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod/v3'
import { Mail } from 'lucide-react'

import { Button } from '@bnsn/ui/components/button'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@bnsn/ui/components/form'
import { Input } from '@bnsn/ui/components/input'
import { cn } from '@bnsn/ui/lib/utils'

const formSchema = z.object({
  email: z.string().min(1, { message: 'Email is required' }).email({ message: 'Please enter a valid email address' }),
})

export interface PasswordlessLoginFormProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSubmit'> {
  onSubmit: (values: z.infer<typeof formSchema>) => void
  title?: string
  description?: string
  submitText?: string
  isLoading?: boolean
}

export function PasswordlessLoginForm({
  onSubmit,
  title = 'Sign in',
  description = 'Enter your email to sign in or create an account',
  submitText = 'Continue with Email',
  isLoading = false,
  className,
  ...props
}: PasswordlessLoginFormProps) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  })

  function handleSubmit(values: z.infer<typeof formSchema>) {
    onSubmit(values)
  }

  return (
    <div className={cn('w-full space-y-6', className)} {...props}>
      {title && <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>}
      {description && <p className="text-sm text-muted-foreground">{description}</p>}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="name@example.com" autoComplete="email" {...field} />
                </FormControl>
                <FormDescription>We&apos;ll send you a magic link to sign in</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <div className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                <span>Sending link...</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                <span>{submitText}</span>
              </div>
            )}
          </Button>
        </form>
      </Form>
    </div>
  )
}
